// Login page - 使用内联+useEffect方式，仅客户端渲染，避免水合问题
'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Lock, Mail, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { queryClient } from '@/components/common/Providers';
import { useScreenSize } from '@/hooks/useMobile';

import { getEnv } from '@/lib/api';
import DeviceApplyModal from '@/components/device/DeviceApplyModal';

export default function LoginPage() {
  const [mounted, setMounted] = useState(false);
  const [env, setEnv] = useState('dev');

  useEffect(() => {
    setMounted(true);
    setEnv(getEnv());
  }, []);

  if (!mounted) {
    return <div style={{
      position: 'relative', minHeight: '100vh', display: 'flex',
      overflow: 'hidden', backgroundColor: '#0a0a1a', color: '#ededed',
      alignItems: 'center', justifyContent: 'center'
    }}><div style={{ color: '#6366f1', fontSize: '18px' }}>Loading...</div></div>;
  }

  return <LoginForm env={env} />;
}

function LoginForm({ env }: { env: string }) {
  const router = useRouter();
  const { login, token } = useAuthStore();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deviceError, setDeviceError] = useState('');
  const [showDeviceApply, setShowDeviceApply] = useState(false);
  const { isMobile } = useScreenSize();

  // Get redirect param from URL
  const [redirectTo, setRedirectTo] = useState('');
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const r = params.get('redirect');
    if (r) setRedirectTo(r);
  }, []);

  // Redirect if already authenticated (has token)
  useEffect(() => {
    if (token) {
      router.replace('/dashboard');
    }
  }, [token, router]);

  // Canvas particle animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationId: number;
    let particles: Array<{ x: number; y: number; vx: number; vy: number; size: number; opacity: number }> = [];
    const particleCount = 130;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    for (let i = 0; i < particleCount; i++) {
      particles.push({ x: Math.random() * canvas.width, y: Math.random() * canvas.height, vx: (Math.random() - 0.5) * 0.5, vy: (Math.random() - 0.5) * 0.5, size: Math.random() * 3 + 1.5, opacity: Math.random() * 0.6 + 0.15 });
    }
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p, i) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(99, 102, 241, ${p.opacity})`;
        ctx.fill();
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[j].x - p.x;
          const dy = particles[j].y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 200) {
            ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(99, 102, 241, ${0.2 * (1 - dist / 200)})`; ctx.lineWidth = 1.2; ctx.stroke();
          }
        }
      });
      animationId = requestAnimationFrame(animate);
    };
    animate();
    return () => { cancelAnimationFrame(animationId); window.removeEventListener('resize', resize); };
  }, []);

  const handleLogin = async (e: any) => {
    e?.preventDefault?.();
    const errDiv = errorRef.current;
    if (errDiv) { errDiv.style.display = 'none'; errDiv.textContent = ''; }
    if (!email || !password) {
      if (errDiv) { errDiv.textContent = '请输入邮箱和密码'; errDiv.style.display = 'block'; }
      return;
    }
    setIsLoading(true);
    try {
      // 设备绑定：附带 device_id + 指纹
      const { getDeviceId, getFingerprint, getDeviceName } = await import('@/lib/device');
      await login(email, password, {
        device_id: getDeviceId(),
        fingerprint: getFingerprint(),
        device_name: getDeviceName(),
      });
      // 清除 React Query 缓存，避免切换用户时看到旧数据
      queryClient.clear();
      // 确保 token 写入 localStorage（apiClient 从 localStorage 读）
      const authState = useAuthStore.getState();
      if (authState.token) {
        localStorage.setItem('rwai-auth', JSON.stringify({
          state: {
            token: authState.token,
            user: authState.user,
            permissions: authState.permissions,
            isSuperuser: authState.isSuperuser,
          },
          version: 0,
        }));
      }
      router.push(redirectTo || '/dashboard');
    } catch (err: any) {
      setIsLoading(false);
      const msg = err?.message || '登录失败';
      // 设备拦截：显示申请入口
      if (msg.startsWith('DEVICE_BLOCKED') || msg.startsWith('DEVICE_PENDING') || msg.startsWith('DEVICE_EXPIRED')) {
        const reason = msg.split('|')[1] || msg;
        setDeviceError(reason);
        setShowDeviceApply(true);
        if (errDiv) { errDiv.textContent = reason; errDiv.style.display = 'block'; }
      } else {
        if (errDiv) { errDiv.textContent = msg; errDiv.style.display = 'block'; }
      }
    }
  };

  // 动态获取 basePath（与 next.config.ts 中的一致）
  const getBasePath = () => {
    if (typeof window === 'undefined') return process.env.NEXT_PUBLIC_BASE_PATH || '/rwai';
    return process.env.NEXT_PUBLIC_BASE_PATH || '';
  };

  const basePath = getBasePath();

  return (
    <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', overflow: 'hidden', backgroundColor: '#0a0a1a', color: '#ededed' }}>
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, zIndex: 0 }} />
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: 'linear-gradient(135deg, rgba(10,10,26,0.8), transparent, rgba(10,10,26,0.8))' }} />
      <div style={{ position: 'absolute', top: '-30%', left: '-20%', width: '60%', height: '60%', background: 'radial-gradient(circle, rgba(99,102,241,0.2), transparent)', borderRadius: '50%', filter: 'blur(120px)', zIndex: 1 }} />
      <div style={{ position: 'absolute', bottom: '-20%', right: '-10%', width: '50%', height: '50%', background: 'radial-gradient(circle, rgba(129,140,248,0.15), transparent)', borderRadius: '50%', filter: 'blur(100px)', zIndex: 1 }} />
      <div style={{ position: 'absolute', top: '33%', right: '25%', width: '1px', height: '200px', background: 'linear-gradient(to bottom, transparent, rgba(99,102,241,0.3), transparent)', zIndex: 1 }} />

      <div style={{ position: 'relative', zIndex: 10, display: 'flex', width: '100%' }}>
        {/* 设备绑定申请弹窗 */}
        <DeviceApplyModal
          open={showDeviceApply}
          onClose={() => setShowDeviceApply(false)}
        />
        {/* Left — Brand */}
        <div style={{ flex: 1, display: isMobile ? 'none' : 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 64px' }}>
          <div style={{ maxWidth: '512px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <img src={`${basePath}/images/logo.png`} alt="RoundWhale" style={{ height: '40px', width: 'auto' }} />
              <span style={{ fontSize: '24px', fontWeight: 700, color: '#ffffff' }}>RWAI</span>
            </div>
              <h1 style={{ fontSize: '48px', fontWeight: 700, color: '#ffffff', lineHeight: 1.25, marginBottom: '24px' }}>
              Roundwhale<br />
              <span style={{ background: 'linear-gradient(90deg, #6366f1, #818cf8, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{env === 'prod' ? 'Enterprise OS' : 'Enterprise OS（开发模式）'}</span>
            </h1>
            <p style={{ fontSize: '18px', color: '#9ca3af', lineHeight: 1.625 }}>
              All-in-one engine for Chat, CRM, Projects, OA, Workflow & more.<br />
              Built for agility. Powered by AI.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '32px' }}>
              {['CRM', 'Chat', 'AI', 'Projects', 'Tasks', 'Files'].map((tag) => (
                <span key={tag} style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 500, color: '#9ca3af', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '9999px' }}>{tag}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Right — Login form */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '24px 16px' : '48px 24px' }}>
          <div style={{ width: '100%', maxWidth: '420px' }}>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', inset: '-2px', background: 'linear-gradient(135deg, rgba(99,102,241,0.4), transparent, rgba(129,140,248,0.4))', borderRadius: '16px', filter: 'blur(4px)', opacity: 0.6 }} />
              <div style={{ position: 'relative', backgroundColor: 'rgba(17,17,37,0.9)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: isMobile ? '24px 20px' : '32px' }}>
                {/* Logo mobile */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '32px' }}>
                  <img src={`${basePath}/images/logo.png`} alt="RoundWhale" style={{ height: '28px', width: 'auto' }} />
                  <span style={{ fontSize: '18px', fontWeight: 700, color: '#ffffff' }}>RWAI</span>
                </div>

                <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#ffffff', marginBottom: '4px' }}>Welcome back</h2>
                <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '32px' }}>Sign in to your account to continue</p>

                <div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#d1d5db', marginBottom: '6px' }}>Email</label>
                    <div style={{ position: 'relative' }}>
                      <Mail style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#6b7280' }} />
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email"
                        style={{ width: '100%', padding: '10px 40px 10px 40px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '14px', color: '#ffffff', outline: 'none', boxSizing: 'border-box' }} />
                    </div>
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, color: '#d1d5db', marginBottom: '6px' }}>Password</label>
                    <div style={{ position: 'relative' }}>
                      <Lock style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#6b7280' }} />
                      <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password"
                        style={{ width: '100%', padding: '10px 40px 10px 40px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '14px', color: '#ffffff', outline: 'none', boxSizing: 'border-box' }} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280', cursor: 'pointer', border: 'none', background: 'none', padding: 0 }}>
                        {showPassword ? <EyeOff style={{ width: '16px', height: '16px' }} /> : <Eye style={{ width: '16px', height: '16px' }} />}
                      </button>
                    </div>
                  </div>

                  <button type="button" disabled={isLoading} onClick={(e) => handleLogin(e)}
                    onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
                    style={{ width: '100%', height: isMobile ? 48 : 44, background: 'linear-gradient(135deg, #6366f1, #818cf8)', border: 'none', borderRadius: '8px', color: '#ffffff', fontSize: '14px', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.7 : 1, boxShadow: isHovered ? '0 10px 15px -3px rgba(99,102,241,0.3)' : 'none' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {isLoading ? 'Signing in...' : 'Sign in'}
                      <ArrowRight style={{ width: '16px', height: '16px', transition: 'transform 0.2s', transform: isHovered ? 'translateX(2px)' : 'translateX(0)' }} />
                    </span>
                  </button>

                  <div ref={errorRef} style={{ display: 'none', marginTop: '12px', padding: '10px 14px', backgroundColor: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: '8px', color: '#fca5a5', fontSize: '13px', textAlign: 'center' }}></div>
                </div>

                <div style={{ marginTop: '24px', textAlign: 'center' }}>
                  <p style={{ fontSize: '12px', color: '#6b7280' }}>AI powered OS @ Roundwhale</p>
                  <p style={{ fontSize: '11px', color: '#4b5563', marginTop: '4px' }}>
                    <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" style={{ color: '#4b5563', textDecoration: 'none' }}>粤ICP备2026080899号</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
